import type { ElementType, ReactNode } from 'react';
import type { To } from 'react-router';

import { colors, darkThemeSelector, styled } from '../../../stitches.config';
import { Small } from '../../../text/Small';
import { chassisVars } from './utils';

const ChassisUnitLabelText = styled('span', {
  position: 'absolute',
  inset: 0,
  padding: `$4 $${chassisVars.unit.labelInset}`,
  overflow: 'hidden',
  fontSize: '$11',
  lineHeight: `$${chassisVars.unit.labelLine}`,
  textOverflow: 'ellipsis',
  transform: 'rotate(180deg)',
  whiteSpace: 'nowrap',
  writingMode: 'vertical-lr',
});
ChassisUnitLabelText.displayName = 'ChassisUnitLabelText';

const ChassisUnitLabel = styled(Small, {
  position: 'relative',
  alignSelf: 'stretch',
  widthAll: `$${chassisVars.unit.label}`,
  background: colors.bgNeutralLight,
  fontWeight: '$bold',
  overflow: 'hidden',

  [darkThemeSelector]: {
    background: colors.bgNeutralDark,
  },

  variants: {
    hasLEDs: {
      true: {
        borderRadius: '$6 0 0 $6',
      },
      false: {
        borderRadius: '$6',
      },
    },
  },
});
ChassisUnitLabel.displayName = 'ChassisUnitLabel';

const ChassisUnitLEDs = styled('div', {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  widthAll: `$${chassisVars.unit.label}`,
  padding: `$4 $${chassisVars.unit.labelInset}`,
  background: colors.bgNeutralLight,

  [darkThemeSelector]: {
    background: colors.bgNeutralDark,
  },

  variants: {
    hasLabel: {
      true: {
        borderRadius: '0 $6 $6 0',
      },
      false: {
        borderRadius: '$6',
      },
    },
  },
});
ChassisUnitLEDs.displayName = 'ChassisUnitLEDs';

const ChassisUnitDetails = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$2',
  height: `100%`,
});
ChassisUnitDetails.displayName = 'ChassisUnitDetails';

const ChassisUnitContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: `$${chassisVars.gap.slot}`,
  height: `100%`,
});
ChassisUnitContainer.displayName = 'ChassisUnitContainer';

export type ChassisUnitLabelProps = {
  content: ReactNode;
  // Routing
  as?: ElementType;
  to?: To;
};

export type ChassisUnitProps = {
  children?: ReactNode;
  label?: ChassisUnitLabelProps;
  leds?: ReactNode;
};

export function ChassisUnit({ label, leds, children }: ChassisUnitProps) {
  const name = typeof label?.content === 'string' ? label.content : undefined;
  const hasLabel = !!label;
  const hasLEDs = !!leds;
  const hasDetails = hasLabel || hasLEDs;

  return (
    <ChassisUnitContainer>
      {hasDetails && (
        <ChassisUnitDetails>
          {hasLabel && (
            <ChassisUnitLabel
              as={label.as}
              to={label.to}
              title={name !== undefined ? `${name}` : undefined}
              family="monospace"
              weight="bold"
              hasLEDs={hasLEDs}
            >
              <ChassisUnitLabelText>{label.content}</ChassisUnitLabelText>
            </ChassisUnitLabel>
          )}
          {hasLEDs && <ChassisUnitLEDs hasLabel={hasLabel}>{leds}</ChassisUnitLEDs>}
        </ChassisUnitDetails>
      )}
      {children}
    </ChassisUnitContainer>
  );
}
